/**
 * User Values Context
 * 
 * Provides global state management for user value preferences.
 * Handles dual context (USA/Canada) preferences, value definitions,
 * and products matching user's saved values.
 * 
 * @author OriginSelect Team
 * @created December 1, 2025
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useUser } from './UserContext';
import { getStoredGoogleIdToken } from '../utils/googleAuth';
import {
  fetchUSAPrimaryValues,
  fetchCanadaPrimaryValues,
  fetchUSAStates,
  fetchCanadianProvinces
} from '../api/publicApi';
import {
  fetchUserPreferences,
  updateUserPreferences,
  patchUserPreferences,
  clearUserPreferences,
  fetchProductsByValues
} from '../api/collectionsApi';

const UserValuesContext = createContext(null);

// Context types
const CONTEXTS = {
  USA: 'USA',
  CA: 'CA'
};

export const UserValuesProvider = ({ children }) => {
  const { user } = useUser();
  
  // Current context (USA or CA)
  const [currentContext, setCurrentContext] = useState(CONTEXTS.USA);
  
  // Value definitions (loaded once)
  const [usaValues, setUsaValues] = useState([]);
  const [canadaValues, setCanadaValues] = useState([]);
  const [usaStates, setUsaStates] = useState([]);
  const [canadaProvinces, setCanadaProvinces] = useState([]);
  const [valuesLoading, setValuesLoading] = useState(true);
  
  // User preferences (separate for each context)
  const [preferences, setPreferences] = useState({
    USA: { preferredValues: [], preferredRegions: [], updatedAt: null },
    CA: { preferredValues: [], preferredRegions: [], updatedAt: null }
  });
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [preferencesError, setPreferencesError] = useState(null);
  
  // Products by values
  const [matchedProducts, setMatchedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsPagination, setProductsPagination] = useState({
    hasMore: false,
    nextToken: null,
    total: 0
  });
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Load value definitions on mount
  useEffect(() => {
    const loadValueDefinitions = async () => {
      setValuesLoading(true);
      try {
        const results = await Promise.allSettled([
          fetchUSAPrimaryValues(),
          fetchCanadaPrimaryValues(),
          fetchUSAStates(),
          fetchCanadianProvinces()
        ]);
        
        const [usaRes, caRes, usaStatesRes, caProvincesRes] = results;
        
        if (usaRes.status === 'fulfilled') setUsaValues(usaRes.value.values || []);
        else console.error('Failed to load USA values:', usaRes.reason);
        
        if (caRes.status === 'fulfilled') setCanadaValues(caRes.value.values || []);
        else console.error('Failed to load Canada values:', caRes.reason);
        
        if (usaStatesRes.status === 'fulfilled') setUsaStates(usaStatesRes.value.states || []);
        else console.error('Failed to load USA states:', usaStatesRes.reason);
        
        if (caProvincesRes.status === 'fulfilled') setCanadaProvinces(caProvincesRes.value.provinces || []);
        else console.error('Failed to load Canada provinces:', caProvincesRes.reason);
      } catch (err) {
        console.error('Failed to load value definitions:', err);
      } finally {
        setValuesLoading(false);
      }
    };

    loadValueDefinitions();
  }, []);

  // Load user preferences when user changes
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!user?.email) {
        setPreferences({
          USA: { preferredValues: [], preferredRegions: [], updatedAt: null },
          CA: { preferredValues: [], preferredRegions: [], updatedAt: null }
        });
        return;
      }

      // Check if Google ID token is available (user is authenticated)
      const googleIdToken = typeof window !== 'undefined' ? getStoredGoogleIdToken() : null;
      if (!googleIdToken) {
        console.warn('⚙️ UserValuesContext: Waiting for Google authentication...');
        setPreferences({
          USA: { preferredValues: [], preferredRegions: [], updatedAt: null },
          CA: { preferredValues: [], preferredRegions: [], updatedAt: null }
        });
        return;
      }

      setPreferencesLoading(true);
      setPreferencesError(null);
      
      try {
        const response = await fetchUserPreferences(user.email, 'all');
        if (response.preferences) {
          setPreferences({
            USA: response.preferences.USA || { preferredValues: [], preferredRegions: [], updatedAt: null },
            CA: response.preferences.CA || { preferredValues: [], preferredRegions: [], updatedAt: null }
          });
        }
      } catch (err) {
        console.error('Failed to load user preferences:', err);
        setPreferencesError(err.message);
      } finally {
        setPreferencesLoading(false);
      }
    };

    loadUserPreferences();
  }, [user?.email]);

  // Get current context's values and regions
  const currentValues = useMemo(() => {
    return currentContext === CONTEXTS.CA ? canadaValues : usaValues;
  }, [currentContext, canadaValues, usaValues]);

  const currentRegions = useMemo(() => {
    return currentContext === CONTEXTS.CA ? canadaProvinces : usaStates;
  }, [currentContext, canadaProvinces, usaStates]);

  const currentPreferences = useMemo(() => {
    return preferences[currentContext];
  }, [currentContext, preferences]);

  // Group values by category
  const groupedValues = useMemo(() => {
    const groups = {
      ownership: [],
      sustainability: [],
      social: []
    };
    
    currentValues.forEach(value => {
      if (groups[value.group]) {
        groups[value.group].push(value);
      }
    });
    
    return groups;
  }, [currentValues]);

  // Switch context between USA and Canada
  const switchContext = useCallback((newContext) => {
    if (newContext === CONTEXTS.USA || newContext === CONTEXTS.CA) {
      setCurrentContext(newContext);
      setMatchedProducts([]); // Clear products when switching
      setNeedsOnboarding(false);
    }
  }, []);

  // Toggle a value in preferences
  const toggleValue = useCallback((valueCode) => {
    setPreferences(prev => {
      const contextPrefs = prev[currentContext];
      const currentlySelected = contextPrefs.preferredValues.includes(valueCode);
      
      return {
        ...prev,
        [currentContext]: {
          ...contextPrefs,
          preferredValues: currentlySelected
            ? contextPrefs.preferredValues.filter(v => v !== valueCode)
            : [...contextPrefs.preferredValues, valueCode]
        }
      };
    });
  }, [currentContext]);

  // Toggle a region in preferences
  const toggleRegion = useCallback((regionCode) => {
    setPreferences(prev => {
      const contextPrefs = prev[currentContext];
      const currentlySelected = contextPrefs.preferredRegions.includes(regionCode);
      
      return {
        ...prev,
        [currentContext]: {
          ...contextPrefs,
          preferredRegions: currentlySelected
            ? contextPrefs.preferredRegions.filter(r => r !== regionCode)
            : [...contextPrefs.preferredRegions, regionCode]
        }
      };
    });
  }, [currentContext]);

  // Set all values at once
  const setValues = useCallback((valueCodes) => {
    setPreferences(prev => ({
      ...prev,
      [currentContext]: {
        ...prev[currentContext],
        preferredValues: valueCodes
      }
    }));
  }, [currentContext]);

  // Set all regions at once
  const setRegions = useCallback((regionCodes) => {
    setPreferences(prev => ({
      ...prev,
      [currentContext]: {
        ...prev[currentContext],
        preferredRegions: regionCodes
      }
    }));
  }, [currentContext]);

  // Save preferences to backend
  const savePreferences = useCallback(async () => {
    if (!user?.email) {
      throw new Error('Must be logged in to save preferences');
    }

    setPreferencesSaving(true);
    setPreferencesError(null);

    try {
      const response = await updateUserPreferences({
        userId: user.email,
        context: currentContext,
        preferredValues: preferences[currentContext].preferredValues,
        preferredRegions: preferences[currentContext].preferredRegions
      });

      // Update local state with server response
      setPreferences(prev => ({
        ...prev,
        [currentContext]: {
          preferredValues: response.preferredValues || [],
          preferredRegions: response.preferredRegions || [],
          updatedAt: response.updatedAt
        }
      }));

      return true;
    } catch (err) {
      console.error('Failed to save preferences:', err);
      setPreferencesError(err.message);
      throw err;
    } finally {
      setPreferencesSaving(false);
    }
  }, [user?.email, currentContext, preferences]);

  // Reset preferences for current context
  const resetPreferences = useCallback(async () => {
    if (!user?.email) return;

    try {
      await clearUserPreferences(user.email, currentContext);
      setPreferences(prev => ({
        ...prev,
        [currentContext]: {
          preferredValues: [],
          preferredRegions: [],
          updatedAt: null
        }
      }));
      setMatchedProducts([]);
      setNeedsOnboarding(true);
    } catch (err) {
      console.error('Failed to reset preferences:', err);
      throw err;
    }
  }, [user?.email, currentContext]);

  // Load products matching user's SAVED values (requires preferences to be saved first)
  const loadMatchedProducts = useCallback(async (options = {}) => {
    if (!user?.email) {
      setMatchedProducts([]);
      setNeedsOnboarding(true);
      return;
    }

    setProductsLoading(true);
    
    try {
      // Backend reads from saved preferences in DynamoDB
      console.log('📦 Fetching products by values:', {
        userId: user.email,
        context: currentContext,
        limit: options.limit || 24
      });
      
      const response = await fetchProductsByValues({
        userId: user.email,
        context: currentContext,
        limit: options.limit || 24,
        nextToken: options.nextToken,
        matchMode: options.matchMode || 'any'
      });

      console.log('📦 Products by values response:', response);
      console.log('📦 Response keys:', Object.keys(response));
      console.log('📦 Response.data:', response.data);
      console.log('📦 Response.products:', response.products);

      // Handle different possible response structures
      const products = response.data || response.products || [];
      
      if (response.metadata?.message || !products.length) {
        // No preferences saved or no products found
        setNeedsOnboarding(!preferences[currentContext].updatedAt);
        setMatchedProducts([]);
      } else {
        setNeedsOnboarding(false);
        if (options.append && options.nextToken) {
          setMatchedProducts(prev => [...prev, ...products]);
        } else {
          setMatchedProducts(products);
        }
      }

      setProductsPagination({
        hasMore: response.pagination?.hasMore || false,
        nextToken: response.pagination?.nextToken || null,
        total: response.pagination?.total || 0
      });

      return response;
    } catch (err) {
      console.error('Failed to load matched products:', err);
      setMatchedProducts([]);
      throw err;
    } finally {
      setProductsLoading(false);
    }
  }, [user?.email, currentContext, preferences]);

  // Load more products
  const loadMoreProducts = useCallback(async () => {
    if (!productsPagination.hasMore || !productsPagination.nextToken) return;
    
    return loadMatchedProducts({
      nextToken: productsPagination.nextToken,
      append: true
    });
  }, [productsPagination, loadMatchedProducts]);

  // Check if a value is selected
  const isValueSelected = useCallback((valueCode) => {
    return preferences[currentContext].preferredValues.includes(valueCode);
  }, [currentContext, preferences]);

  // Check if a region is selected
  const isRegionSelected = useCallback((regionCode) => {
    return preferences[currentContext].preferredRegions.includes(regionCode);
  }, [currentContext, preferences]);

  // Check if preferences have been modified (unsaved changes)
  const hasUnsavedChanges = useMemo(() => {
    // This is a simple check - could be enhanced with deep comparison
    return preferences[currentContext].updatedAt === null && 
           (preferences[currentContext].preferredValues.length > 0 || 
            preferences[currentContext].preferredRegions.length > 0);
  }, [currentContext, preferences]);

  const value = {
    // Context
    currentContext,
    switchContext,
    CONTEXTS,
    
    // Value definitions
    usaValues,
    canadaValues,
    currentValues,
    groupedValues,
    valuesLoading,
    
    // Region definitions
    usaStates,
    canadaProvinces,
    currentRegions,
    
    // User preferences
    preferences,
    currentPreferences,
    preferencesLoading,
    preferencesSaving,
    preferencesError,
    hasUnsavedChanges,
    
    // Preference actions
    toggleValue,
    toggleRegion,
    setValues,
    setRegions,
    savePreferences,
    resetPreferences,
    isValueSelected,
    isRegionSelected,
    
    // Products by values
    matchedProducts,
    productsLoading,
    productsPagination,
    needsOnboarding,
    loadMatchedProducts,
    loadMoreProducts,
    
    // User state
    isLoggedIn: !!user?.email
  };

  return (
    <UserValuesContext.Provider value={value}>
      {children}
    </UserValuesContext.Provider>
  );
};

// Custom hook to use user values context
export const useUserValues = () => {
  const context = useContext(UserValuesContext);
  if (!context) {
    throw new Error('useUserValues must be used within a UserValuesProvider');
  }
  return context;
};

export default UserValuesContext;
